import { useState } from 'react';
import { useSelector } from 'react-redux';
import RowV2 from '@/components/v2/RowV2';
import getArgNestedLevel from '@/lib/arguments/getArgNestedLevel';
import { CollapseIcon, ExpandIcon } from '@/lib/constants/icons';
import dataTypeTitles from '@/screens/workflows/schema/dataTypeTitles';
import dataTypeIcons from '@/screens/workflows/schema/dataTypeIcons';
import ArgumentInputRowLabel from './ArgumentInputRowLabel';
import ArgumentCustomArguments from './ArgumentCustomArguments';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import ArgumentInputRowValue from './ArgumentInputRowValue';
import ArgumentMultipleValuesWrapper from './ArgumentMultipleValuesWrapper';
import ArgumentWrapper from './ArgumentWrapper.jsx';
import ArgumentLabel from './ArgumentLabel.jsx';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';

const getChildrenKey = (child, idx, args) => {
  const lastBracketIndex = child?.key?.lastIndexOf(']');
  if (lastBracketIndex === -1) {
    return child?.id ? `${child?.id}-${idx}` : idx;
  }
  const parentArrayKey = child?.key?.substring(0, lastBracketIndex + 1);
  const currentValueId = args?.[parentArrayKey]?.id;
  return currentValueId ? `${currentValueId}-${child?.id || idx}` : child?.id || idx;
};

const ArgumentInputRow = ({ argument, definitions, definition, dataSource, lookupActionId, overrideAction, dataSourcePath, dataSavePath, disabled = false, simpleArguments = false, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const argumentValueTypeIsLexical = useSelector(memoizeSelector(`argumentValueType-${argument?.key}`, (state) => argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key]?.type === 'lexical'));
  const isRequired = useSelector(
    memoizeSelector(`isRequired-${argument?.key}`, (state) => {
      const exceptions = definitions?.filter(({ id }) => argument?.requiredExceptions?.includes(id));
      return argument?.required && exceptions?.length ? exceptions?.every((n) => !argumentSelectors[dataSource].activeArguments()(state)?.[n.key]) : argument?.required;
    })
  );
  const childRows = useSelector(
    memoizeSelector(`childRows-${argument?.key}`, (state) => {
      const values = argumentSelectors[dataSource].activeArguments()(state);
      return argument?.children?.map((child, idx) => ({
        key: getChildrenKey(child, idx, values),
        argument: child,
        definitions
      }));
    })
  );

  const hasChildren = argument?.children?.length > 0;
  const hasCustomArguments = argument?.customArguments === true;
  const isRawObject = ['array', 'object'].includes(argument?.dataType) && argumentValueTypeIsLexical;
  const isNotArrayOfObjects = argument?.dataType === 'array' && argument?.itemDataType && argument?.itemDataType !== 'object';
  const hasMultipleValues = !isRawObject && (argument?.multipleValues?.isActive === true || isNotArrayOfObjects);

  const icon = argument?.componentIcon ? argument?.componentIcon : hasChildren && !isRawObject ? (isExpanded ? CollapseIcon : ExpandIcon) : dataTypeIcons[argument?.dataType || 'string'];
  const iconTitle = argument?.componentIcon ? 'Custom Field' : argument?.custom ? `Custom ${dataTypeTitles[argument?.dataType || 'string']} Field` : dataTypeTitles[argument?.dataType || 'string'];

  const argLevel = getArgNestedLevel(argument?.key);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const noLabelException = ['NetSuiteColumnSelector', 'NetSuiteSavedSearchLink', 'GraphQlColumnSelector'].includes(argument?.component);

  // This is when we are rendering arguments that don't have children nor multiple values in any argument of the action
  if (simpleArguments) {
    return (
      <>
        <ArgumentWrapper className={`simple-arguments ${className}`} data-argument-key={argument?.key}>
          {!noLabelException && <ArgumentLabel className="small-gap" argKey={argument?.key} label={argument.name} description={argument.description} variant={argument.variant} isId={argument.isId} custom={argument?.custom} icon={icon} isRequired={isRequired} title={iconTitle} dataSource={dataSource} lookupActionId={lookupActionId} overrideAction={overrideAction} />}
          {hasMultipleValues && <ArgumentMultipleValuesWrapper argument={argument} definition={definition} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} disabled={disabled} simpleArguments />}
          {!hasMultipleValues && <ArgumentInputRowValue disabled={disabled} definition={definition} argument={argument} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} simpleArguments />}
        </ArgumentWrapper>
        {hasCustomArguments && isExpanded && !isRawObject && <ArgumentCustomArguments isSimpleArguments path={'arguments'} parentKey={argument?.key} level={argLevel + 1} definition={definition} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} disabled={disabled} />}
      </>
    );
  }

  return (
    <>
      <RowV2 variant={`workflow-panel level-${argLevel} ${className}`} onClick={hasChildren && !isRawObject ? toggleExpanded : undefined}>
        {!noLabelException && <ArgumentInputRowLabel description={argument.description} icon={icon} isRequired={isRequired} label={argument.name} title={iconTitle} custom={argument?.custom} />}
        {hasMultipleValues && <ArgumentMultipleValuesWrapper argument={argument} definition={definition} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} disabled={disabled} />}
        {!hasMultipleValues && <ArgumentInputRowValue disabled={disabled} definition={definition} argument={argument} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} />}
      </RowV2>
      {hasChildren && isExpanded && !isRawObject && childRows.map(({ key, argument, definitions }) => <ArgumentInputRow key={key} argument={argument} definitions={definitions} definition={definition} dataSource={dataSource} lookupActionId={lookupActionId} overrideAction={overrideAction} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} disabled={disabled} className={className} />)}
      {hasCustomArguments && isExpanded && !isRawObject && <ArgumentCustomArguments path={'arguments'} parentKey={argument?.key} level={argLevel + 1} definition={definition} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} disabled={disabled} className={className} />}
    </>
  );
};

export default ArgumentInputRow;
